import {
  type ChangeEvent,
  type ChangeEventHandler,
  type HTMLInputAutoCompleteAttribute,
  type Ref,
  useState,
} from 'react';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { getPasswordMatch, getPasswordRules } from '@/utils/validation';
import FormError from '@/components/FormError';
import IcEyeClosed from '@/assets/icons/EyeClosed.svg?react';
import IcEyeOpened from '@/assets/icons/EyeOpened.svg?react';
import IcCheck from '@/assets/icons/Check.svg?react';
import IcCross from '@/assets/icons/Cross.svg?react';
import styles from './FormPassword.module.scss';
import { DEFAULT_ANIMATION_DURATION } from '@/config';

type PasswordRulesResults =
  | {
      hasLength: string | boolean | null;
      hasUpperAndLowerCase: string | boolean | null;
      hasNumber: string | boolean | null;
      hasSymbol: string | boolean | null;
    }
  | {
      mustMatch: string | boolean | null;
    };

type FormPasswordProps = {
  autoComplete?: HTMLInputAutoCompleteAttribute;
  className?: string;
  defaultValue?: string;
  disabled?: boolean;
  errorMessage?: string;
  id: string;
  label: string;
  onChange?: ChangeEventHandler;
  onValueChange?: (value: string) => void;
  placeholder?: string;
};

type ConditionalProps =
  | {
      withRequirements?: 'newPassword';
      confirmPasswordValue?: never;
    }
  | {
      withRequirements: 'confirmPassword';
      confirmPasswordValue: string;
    };

type FinalFormPasswordProps = FormPasswordProps &
  ConditionalProps & {
    ref?: Ref<HTMLInputElement>;
  };

const FormPassword = ({
  autoComplete = 'off',
  className,
  disabled = false,
  errorMessage,
  id,
  label,
  onChange,
  onValueChange,
  placeholder,
  ref,
  withRequirements,
  confirmPasswordValue = '',
  defaultValue,
  ...rest
}: FinalFormPasswordProps) => {
  const { t } = useTranslation(['auth']);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const initialValue = typeof defaultValue === 'string' ? defaultValue : '';
  const [inputValue, setInputValue] = useState<string>(initialValue);

  const passwordRules = getPasswordRules(t);
  const passwordMatch = getPasswordMatch(t, confirmPasswordValue);

  const internalErrors: PasswordRulesResults =
    withRequirements === 'confirmPassword'
      ? {
          mustMatch:
            inputValue.length === 0
              ? null
              : (passwordMatch.validate.mustMatch?.(inputValue) ?? null),
        }
      : {
          hasLength:
            inputValue.length === 0
              ? null
              : (passwordRules.validate.hasLength?.(inputValue) ?? null),
          hasUpperAndLowerCase:
            inputValue.length === 0
              ? null
              : (passwordRules.validate.hasUpperAndLowerCase?.(inputValue) ??
                null),
          hasNumber:
            inputValue.length === 0
              ? null
              : (passwordRules.validate.hasNumber?.(inputValue) ?? null),
          hasSymbol:
            inputValue.length === 0
              ? null
              : (passwordRules.validate.hasSymbol?.(inputValue) ?? null),
        };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange?.(event);
    setInputValue(event.target.value);
    onValueChange?.(event.target.value);
  };

  const toggleShowPassword = () => {
    setShowPassword((prevState) => {
      return !prevState;
    });
  };

  const showRequirements = !!(
    withRequirements &&
    (isFocused || inputValue.length > 0)
  );

  return (
    <>
      <label htmlFor={id} className={styles.label}>
        {label}
      </label>
      <div
        className={classNames(styles.inputWrapper, className, {
          [styles.hasError]: !!errorMessage,
          [styles.withRequirements]: showRequirements,
        })}
      >
        <input
          {...rest}
          autoComplete={autoComplete}
          defaultValue={defaultValue}
          ref={ref}
          disabled={disabled}
          placeholder={placeholder}
          className={styles.input}
          type={showPassword ? 'text' : 'password'}
          id={id}
          onChange={handleChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
        />
        <button
          type="button"
          className={styles.showPasswordToggle}
          aria-label={
            showPassword ? t('auth:password.hide') : t('auth:password.show')
          }
          onClick={toggleShowPassword}
        >
          {showPassword ? (
            <IcEyeOpened className={styles.icon} />
          ) : (
            <IcEyeClosed className={styles.icon} />
          )}
        </button>
      </div>
      {errorMessage && (
        <FormError message={errorMessage} className={styles.formError} />
      )}
      <AnimatePresence>
        {showRequirements && (
          <motion.div
            key="requirements"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{
              duration: DEFAULT_ANIMATION_DURATION,
              ease: 'easeInOut',
            }}
            style={{ overflow: 'hidden' }}
          >
            <div className={styles.indicators} aria-hidden="true">
              {Object.entries(internalErrors).map(([validatorKey, isValid]) => {
                return (
                  <div
                    key={validatorKey}
                    className={classNames(styles.indicator, {
                      [styles.valid]: isValid === true,
                    })}
                  />
                );
              })}
            </div>
            <ul className={styles.requirements}>
              {Object.entries(internalErrors).map(([validatorKey, isValid]) => {
                return (
                  <li
                    key={validatorKey}
                    className={classNames(styles.requirement, {
                      [styles.valid]: isValid === true,
                    })}
                  >
                    <IcCheck
                      className={classNames(styles.icon, styles.check)}
                    />
                    <IcCross
                      className={classNames(styles.icon, styles.cross)}
                    />
                    {t(`auth:passwordRules.${validatorKey}`)}
                  </li>
                );
              })}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default FormPassword;
